export const goApiExample = `package main

import (
	"bytes"
	"crypto/hmac"
	"crypto/sha256"
	"encoding/hex"
	"encoding/json"
	"fmt"
	"io/ioutil"
	"net/http"
	"os"
	"time"

	"github.com/google/uuid"
)

// Configuration constants
const (
	APIBaseURL      = "https://app.reasonshield.com/api/v1"
	APIEndpointPath = "/lis-webhook/vendor"
)

// Environment variables for authentication
var (
	VendorID      = os.Getenv("VENDOR_ID")
	APIKey        = os.Getenv("VENDOR_API_KEY")
	WebhookSecret = os.Getenv("VENDOR_WEBHOOK_SECRET")
)

// RequestPayload defines the structure of the API request
type RequestPayload struct {
	VendorID  string    \`json:"vendor_id"\`
	Timestamp string    \`json:"timestamp"\`
	RequestID string    \`json:"request_id"\`
	TestData  TestData  \`json:"test_data"\`
}

// TestData defines the structure of test data
type TestData struct {
	TestID        string         \`json:"test_id"\`
	PanelType     string         \`json:"panel_type"\`
	PatientInfo   PatientInfo    \`json:"patient_info"\`
	CollectionInfo CollectionInfo \`json:"collection_info"\`
	TestDetails   TestDetails    \`json:"test_details"\`
}

// PatientInfo contains patient-related information
type PatientInfo struct {
	PatientID string \`json:"patient_id"\`
	Age       int    \`json:"age"\`
	Gender    string \`json:"gender"\`
}

// CollectionInfo contains specimen collection information
type CollectionInfo struct {
	CollectionDate string \`json:"collection_date"\`
	SpecimenType   string \`json:"specimen_type"\`
}

// TestDetails contains test results
type TestDetails struct {
	Viruses  []PathogenResult \`json:"viruses"\`
	Bacteria []PathogenResult \`json:"bacteria"\`
}

// PathogenResult represents a single pathogen test result
type PathogenResult struct {
	Name   string \`json:"name"\`
	Result string \`json:"result"\`
}

// SubmitResponse is the response from the submit endpoint
type SubmitResponse struct {
	DataID    string \`json:"data_id"\`
	Status    string \`json:"status"\`
	Message   string \`json:"message"\`
	Timestamp string \`json:"timestamp"\`
}

// StatusResponse is the response from the status endpoint
type StatusResponse struct {
	DataID          string \`json:"data_id"\`
	Status          string \`json:"status"\`
	Message         string \`json:"message"\`
	Timestamp       string \`json:"timestamp"\`
	ReportAvailable bool   \`json:"report_available"\`
	ReportURL       string \`json:"report_url,omitempty"\`
}

// ReportResponse is the response from the report endpoint
type ReportResponse struct {
	DataID     string     \`json:"data_id"\`
	ReportData ReportData \`json:"report_data"\`
}

// ReportData contains the assessment report information
type ReportData struct {
	TestID        string         \`json:"test_id"\`
	AnalysisDate  string         \`json:"analysis_date"\`
	RiskAssessment RiskAssessment \`json:"risk_assessment"\`
}

// RiskAssessment contains risk assessment information
type RiskAssessment struct {
	OverallRisk string \`json:"overall_risk"\`
}

// generateSignature creates an HMAC-SHA256 signature for the payload
func generateSignature(payload []byte, secret string) (string, error) {
	// Create HMAC-SHA256 signature
	h := hmac.New(sha256.New, []byte(secret))
	h.Write(payload)
	
	// Return hex-encoded signature
	return hex.EncodeToString(h.Sum(nil)), nil
}

// submitData sends test data to the API with dual authentication
func submitData(testData TestData) (*SubmitResponse, error) {
	// Create full request payload
	payload := RequestPayload{
		VendorID:  VendorID,
		Timestamp: time.Now().UTC().Format(time.RFC3339),
		RequestID: uuid.New().String(),
		TestData:  testData,
	}
	
	// Convert payload to JSON
	payloadBytes, err := json.Marshal(payload)
	if err != nil {
		return nil, fmt.Errorf("error marshaling payload: %w", err)
	}
	
	// Generate signature
	signature, err := generateSignature(payloadBytes, WebhookSecret)
	if err != nil {
		return nil, fmt.Errorf("error generating signature: %w", err)
	}
	
	// Create HTTP request with both authentication methods
	url := fmt.Sprintf("%s%s/submit-data", APIBaseURL, APIEndpointPath)
	req, err := http.NewRequest("POST", url, bytes.NewBuffer(payloadBytes))
	if err != nil {
		return nil, fmt.Errorf("error creating request: %w", err)
	}
	
	// Set headers with both authentication methods
	req.Header.Set("Content-Type", "application/json")
	req.Header.Set("Authorization", "Bearer "+APIKey)        // API Key Authentication
	req.Header.Set("X-Vendor-Signature", signature)          // Signature Verification
	
	// Send request
	client := &http.Client{}
	resp, err := client.Do(req)
	if err != nil {
		return nil, fmt.Errorf("error sending request: %w", err)
	}
	defer resp.Body.Close()
	
	// Read response body
	body, err := ioutil.ReadAll(resp.Body)
	if err != nil {
		return nil, fmt.Errorf("error reading response: %w", err)
	}
	
	// Check status code
	if resp.StatusCode != http.StatusAccepted {
		return nil, fmt.Errorf("unexpected status code: %d, body: %s", resp.StatusCode, body)
	}
	
	// Parse response
	var submitResp SubmitResponse
	if err := json.Unmarshal(body, &submitResp); err != nil {
		return nil, fmt.Errorf("error unmarshaling response: %w", err)
	}
	
	return &submitResp, nil
}

// checkStatus checks the processing status of submitted data
func checkStatus(dataID string) (*StatusResponse, error) {
	// Create HTTP request
	url := fmt.Sprintf("%s%s/status/%s", APIBaseURL, APIEndpointPath, dataID)
	req, err := http.NewRequest("GET", url, nil)
	if err != nil {
		return nil, fmt.Errorf("error creating request: %w", err)
	}
	
	// Set authorization header
	req.Header.Set("Authorization", "Bearer "+APIKey)
	
	// Send request
	client := &http.Client{}
	resp, err := client.Do(req)
	if err != nil {
		return nil, fmt.Errorf("error sending request: %w", err)
	}
	defer resp.Body.Close()
	
	// Read response body
	body, err := ioutil.ReadAll(resp.Body)
	if err != nil {
		return nil, fmt.Errorf("error reading response: %w", err)
	}
	
	// Check status code
	if resp.StatusCode != http.StatusOK {
		return nil, fmt.Errorf("unexpected status code: %d, body: %s", resp.StatusCode, body)
	}
	
	// Parse response
	var statusResp StatusResponse
	if err := json.Unmarshal(body, &statusResp); err != nil {
		return nil, fmt.Errorf("error unmarshaling response: %w", err)
	}
	
	return &statusResp, nil
}

// getReport retrieves the report for processed data
func getReport(dataID string) (*ReportResponse, error) {
	// Create HTTP request
	url := fmt.Sprintf("%s%s/report/%s", APIBaseURL, APIEndpointPath, dataID)
	req, err := http.NewRequest("GET", url, nil)
	if err != nil {
		return nil, fmt.Errorf("error creating request: %w", err)
	}
	
	// Set authorization header
	req.Header.Set("Authorization", "Bearer "+APIKey)
	
	// Send request
	client := &http.Client{}
	resp, err := client.Do(req)
	if err != nil {
		return nil, fmt.Errorf("error sending request: %w", err)
	}
	defer resp.Body.Close()
	
	// Read response body
	body, err := ioutil.ReadAll(resp.Body)
	if err != nil {
		return nil, fmt.Errorf("error reading response: %w", err)
	}
	
	// Check status code
	if resp.StatusCode != http.StatusOK {
		return nil, fmt.Errorf("unexpected status code: %d, body: %s", resp.StatusCode, body)
	}
	
	// Parse response
	var reportResp ReportResponse
	if err := json.Unmarshal(body, &reportResp); err != nil {
		return nil, fmt.Errorf("error unmarshaling response: %w", err)
	}
	
	return &reportResp, nil
}

func main() {
	// Create test data
	testData := TestData{
		TestID:    "12345",
		PanelType: "RESPIRATORY",
		PatientInfo: PatientInfo{
			PatientID: "PT-789456",
			Age:       45,
			Gender:    "M",
		},
		CollectionInfo: CollectionInfo{
			CollectionDate: "2024-09-10T13:11:00-06:00",
			SpecimenType:   "nasopharyngeal swab",
		},
		TestDetails: TestDetails{
			Viruses: []PathogenResult{
				{Name: "Influenza A", Result: "Not Detected"},
				{Name: "SARS-CoV-2", Result: "Detected"},
			},
			Bacteria: []PathogenResult{
				{Name: "Streptococcus pneumoniae", Result: "Not Detected"},
				{Name: "Mycoplasma pneumoniae", Result: "Not Detected"},
			},
		},
	}
	
	// Submit the data
	fmt.Println("Submitting test data...")
	submitResp, err := submitData(testData)
	if err != nil {
		fmt.Printf("Error submitting data: %v\\n", err)
		return
	}
	fmt.Printf("Data submitted successfully. ID: %s\\n", submitResp.DataID)
	
	// Get the data ID
	dataID := submitResp.DataID
	
	// Check status (in a real implementation, you'd add a delay or poll)
	fmt.Println("Checking status...")
	statusResp, err := checkStatus(dataID)
	if err != nil {
		fmt.Printf("Error checking status: %v\\n", err)
		return
	}
	fmt.Printf("Status: %s\\n", statusResp.Status)
	
	// If processing is complete and report is available, get the report
	if statusResp.Status == "COMPLETED" && statusResp.ReportAvailable {
		fmt.Println("Retrieving report...")
		reportResp, err := getReport(dataID)
		if err != nil {
			fmt.Printf("Error retrieving report: %v\\n", err)
			return
		}
		fmt.Printf("Retrieved report with risk assessment: %s\\n", 
			reportResp.ReportData.RiskAssessment.OverallRisk)
	} else {
		fmt.Println("Report not yet available.")
	}
}`; 